import { useLayoutEffect, RefObject } from 'react';
import Config from '@config';

interface UseCarouselTabIndexProps {
  slideContainerRef: RefObject<HTMLDivElement | null>;
  slideIndex: number;
}

/**
 * Ensures only the elements inside the currently visible carousel slide
 * are tabbable. All elements in off-screen slides get tabindex="-1".
 *
 * Why this is needed:
 *  - Browsers treat off-screen elements as focusable unless explicitly disabled.
 *  - Tabbing can cause the browser to scroll to hidden slides, breaking UI state.
 *  - React renders are async; children may appear *after* effects run.
 *
 * This hook uses a MutationObserver to guarantee tabindex stays correct
 * even if slides or their children mount later.
 */
export const useCarouselTabIndex = ({ slideContainerRef, slideIndex }: UseCarouselTabIndexProps) => {
  useLayoutEffect(() => {
    const slideContainer = slideContainerRef.current;
    if (!slideContainer) return;

    // Selector for all elements that *can* receive focus (buttons, links, inputs, etc.)
    const focusableSelector = Config.SELECTORS.focusableElements;

    /**
     * Runs whenever:
     *  - slideIndex changes (visible slide changed)
     *  - the DOM inside a slide changes (lazy-loaded children, React re-renders)
     *
     * It ensures only elements in the current slide can be focused.
     */
    const updateTabIndex = () => {
      // Each direct child is one "slide"
      const slides = Array.from(slideContainer.children);

      slides.forEach((slide, index) => {
        const isVisible = index === slideIndex;

        // Gather all focusable elements inside this slide
        const focusables = slide.querySelectorAll<HTMLElement>(focusableSelector);

        focusables.forEach((el) => {
          if (isVisible) {
            /**
             * This slide is visible:
             * - If tabindex was previously set to "-1", remove it.
             * - Removing restores default browser focus behavior.
             */
            if (el.getAttribute('tabindex') === '-1') {
              el.removeAttribute('tabindex');
            }
          } else {
            /**
             * Slide is hidden:
             * - Force tabindex="-1" so the element cannot be reached via Tab.
             * - Critical for preventing accidental tabbing into off-screen slides.
             */
            el.setAttribute('tabindex', '-1');
          }
        });
      });
    };

    // Run immediately on mount + whenever slideIndex changes
    updateTabIndex();

    /**
     * MutationObserver watches for any DOM changes inside slideContainer:
     *  - New children added (e.g., lazy-rendered components)
     *  - React re-renders content in slides
     *  - Hydration differences after SSR
     *
     * This guarantees the tabindex rules remain correct no matter when
     * the actual DOM nodes appear.
     */
    const observer = new MutationObserver(() => {
      updateTabIndex();
    });

    observer.observe(slideContainer, {
      childList: true, // watch slide mounts/unmounts
      subtree: true, // also watch changes *inside* each slide
    });

    // Clean up observer on unmount or when slideIndex changes
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slideIndex, slideContainerRef]);
};
